import { Button, ConfirmModal, ControlledComboBox, Duration } from '../../../../components';
import { useApi, useModalToggle, useReportingTech } from '../../../../hooks';
import useDebounce from '../../../../hooks/useDebounce';
import { useMutation } from '@tanstack/react-query';
import { PersonnelTable } from './PersonnelTable';
import { useFormContext } from 'react-hook-form';
import { useEffect, useState, useContext } from 'react';
import { Localization } from '../../../../config/localization/localization';
import { LanguageSelectorContext } from '../../../../context/LanguageSelectorContext';

import type {
  IGetPersonnelRequest,
  IGetPersonnelResponse,
  IUpdatePersonnelRequest,
  IUpdatePersonnelResponse,
} from '../../../../types';

export interface IPersonnelType {
  id: string;
  name: string;
}

export const Personnel = () => {
  const formContext = useFormContext();
  const { getPersonnel, postPersonnel } = useApi();
  const { reportingTech } = useReportingTech();
  const { selectedLanguage } = useContext(LanguageSelectorContext);
  const localization = new Localization(selectedLanguage);

  const [idToRemove, setIdToRemove] = useState<string>();
  const [mappedPersonnel, setMappedPersonnel] = useState<Array<IPersonnelType>>([]);
  const [query, setQuery] = useState<string>();
  const [selectedPersonnel, setSelectedPersonnel] = useState<IPersonnelType | null>(null);

  const debouncedQuery = useDebounce(query, 400);

  const removeModal = useModalToggle();

  const allPersonnel: Array<IUpdatePersonnelResponse> = formContext.watch('allPersonnel');

  const hasPersonnel = (selectedPersonnel?.name.length ?? -1) <= 0;

  const onPersonnelQuerySuccess = (data: IGetPersonnelResponse) => {
    if (data === null || data?.results === null) return;
    setMappedPersonnel(
      data?.results?.map((result: string) => {
        const idIndex: number = result?.indexOf('^');
        if (idIndex === -1) return { id: '', name: '' };
        return {
          id: result?.substring(idIndex + 1),
          name: result?.substring(0, idIndex),
        } as IPersonnelType;
      })
    );
  };

  const { isLoading: isFetchingPersonnel, mutate: searchPersonnel } = useMutation({
    mutationFn: getPersonnel,
    onSuccess: onPersonnelQuerySuccess,
  });

  const onPersonnelMutationSuccess = (data: IUpdatePersonnelResponse | null) => {
    if (data === null || data?.name === null || data.name.length === 0) return;
    setSelectedPersonnel(null);
    setMappedPersonnel([]);
    formContext?.setValue('allPersonnel', [...formContext?.getValues('allPersonnel'), data]);
    formContext?.setValue('personnelOverTime', '0:00');
    formContext?.setValue('personnelRegularTime', '0:00');
  };

  const { isLoading: isUpdatingPersonnel, mutate: updatePersonnel } = useMutation({
    mutationFn: postPersonnel,
    onSuccess: onPersonnelMutationSuccess,
  });

  useEffect(() => {
    if (!query) return;
    searchPersonnel({
      clientId: reportingTech?.clientId?.toString() ?? '-1',
      marketId: formContext.getValues('marketId'),
      prefixText: query,
    } as IGetPersonnelRequest);
  }, [debouncedQuery, formContext, query, reportingTech?.clientId, searchPersonnel]);

  const onPersonnelTypeChange = (search: string) => setQuery(search);

  const onPersonnelTypeSelect = (p: IPersonnelType | null) => setSelectedPersonnel(p);

  const onAddPersonnel = () => {
    const regular: Array<null | string> | undefined = formContext.getValues('personnelRegularTime')?.split(':');
    const overtime: Array<null | string> | undefined = formContext.getValues('personnelOverTime')?.split(':');

    const model: IUpdatePersonnelRequest = {
      action: 'save',
      claimId: formContext.getValues('claimId'),
      date: Date.now().toString(),
      id: selectedPersonnel?.id ?? '0',
      name: selectedPersonnel?.name ?? '',
      overtime: overtime ? `${overtime[0] ?? 0} hrs,${overtime[1] ?? 0} min` : '0 hrs,0 min',
      regular: regular ? `${regular[0] ?? 0} hrs,${regular[1] ?? 0} min` : '0 hrs,0 min',
    };

    updatePersonnel(model);
  };

  const onRemovePersonnel = (itemId: string) => {
    setIdToRemove(itemId);
    removeModal.open();
  };

  const onRemovePersonnelConfirm = () => {
    const filteredPersonnel = allPersonnel.filter((p) => p.itemId !== idToRemove);
    formContext?.setValue('allPersonnel', [...filteredPersonnel]);

    const model: IUpdatePersonnelRequest = {
      action: 'delete',
      claimId: formContext.getValues('claimId'),
      date: '',
      id: idToRemove ?? '-1',
      name: '',
      overtime: '',
      regular: '',
    };

    updatePersonnel(model);
  };

  return (
    <div className={'flex flex-col items-baseline space-y-2'}>
      {/* PERSONNEL COMBOBOX */}
      <ControlledComboBox
        debounce={debouncedQuery}
        displayKey={'name'}
        idKey={'id'}
        isLoading={isFetchingPersonnel}
        items={mappedPersonnel}
        label={localization.translate('personnel.personnelTypeLabel')}
        onChange={onPersonnelTypeChange}
        onSelect={onPersonnelTypeSelect}
        placeholder={localization.translate('personnel.personnelTypePlaceholder')}
        selectedItem={selectedPersonnel}
        setSelectedItem={setSelectedPersonnel}
        tooltip={localization.translate('personnel.personnelTypeTooltip')}
      />

      <div className={'flex w-full flex-col sm:flex-row sm:space-x-2'}>
        {/* REGULAR HOURS */}
        <Duration
          label={localization.translate('personnel.regularTimeLabel')}
          name={'personnelRegularTime'}
          tooltip={localization.translate('personnel.regularTimeTooltip')}
        />

        {/* OVERTIME HOURS */}
        <Duration
          label={localization.translate('personnel.overtimeLabel')}
          name={'personnelOverTime'}
          tooltip={localization.translate('personnel.overtimeTooltip')}
        />

        {/* ADD A PERSONNEL TYPE  */}
        <div className={'flex pt-2 sm:pt-0'}>
          <Button
            buttonProps={{ isDisabled: hasPersonnel, onPress: onAddPersonnel }}
            className={'w-full place-self-end'}
            isBusy={isUpdatingPersonnel}
          >
            {localization.translate('actions.add')}
          </Button>
        </div>
      </div>

      {/* PERSONNEL TABLE */}
      <PersonnelTable onRemove={onRemovePersonnel} personnel={allPersonnel} />

      {/* CONFIRM REMOVE MODAL */}
      <ConfirmModal
        message={localization.translate('personnel.confirmDeleteMessage')}
        modalToggle={removeModal}
        onConfirm={onRemovePersonnelConfirm}
        title={localization.translate('personnel.confirmDeleteTitle')}
      />
    </div>
  );
};